import styled from "styled-components";
import { Flex } from "antd";
import { TScript } from "pages/media/types/";
import {
  parseScriptBySlides,
  reconstructScriptWithSlide,
} from "pages/media/projects/components/slides/";
import { isRTL } from "helpers/utils/";
import { useTemplateData } from "../../../hooks/useTemplateData";
import { useSlideMutations } from "../../../hooks/useSlideMutations";
import { useStoryboardEditing } from "../../../hooks/useStoryboardEditing";
import { useTemplateEditorStore } from "../../../store/useTemplateEditorStore";
import { Narration } from "../../VoiceoverNarrationCard/components/Narration";
import { PanelSection } from "../PanelSection";
import { SlideScriptPanel } from "./SlideScriptPanel";

export const ScriptSectionsEditor = () => {
  const {
    projectId,
    organizationId,
    storyboardSections,
    templatesData: { storyboard, primaryDocumentId },
    mediaGenerationSettingsData: { markMediaOutOfDate },
  } = useTemplateData();

  const { updateStoryboardSection } = useStoryboardEditing({
    projectId: projectId ?? "",
    organizationId: organizationId ?? "",
    storyboardId: storyboard?.asset_id ?? "",
    primaryDocumentId: primaryDocumentId ?? "",
    markMediaOutOfDate,
  });

  const { removeSlide } = useSlideMutations();

  const setCurrentSlide = useTemplateEditorStore(
    (store) => store.setCurrentSlide
  );

  const save = (sectionId: string, content: string) => {
    updateStoryboardSection(sectionId, { script: { content } }).catch(
      (error) => {
        console.error("Error updating section script", error);
      }
    );
  };

  // Fallback for sections without per-slide [[SLIDE-uuid]] segments (e.g. a
  // transition section, whose script has no slide markers to split by):
  // keep editing the whole section content as a single block, as before.
  const handleWholeSectionEditComplete = (
    sectionId: string,
    script: Partial<TScript>
  ) => {
    const content =
      typeof script.content === "string" && script.content.trim() !== ""
        ? script.content
        : "";
    save(sectionId, content);
  };

  const rtl = isRTL(storyboard?.language ?? "");

  return (
    <>
      {storyboardSections.map((section, index) => {
        const scriptBySlide = parseScriptBySlides(section.script.content);
        const slidesWithScript = section.slides.filter((slide) =>
          scriptBySlide.has(slide.uuid)
        );

        return (
          <PanelSection
            key={section.id}
            title={section.topic}
            noBorder={index === storyboardSections.length - 1}
          >
            <NonInteractiveHighlights>
              {slidesWithScript.length > 0 ? (
                <Flex vertical gap={24}>
                  {slidesWithScript.map((slide) => (
                    <div
                      key={slide.uuid}
                      onFocus={() => setCurrentSlide(slide.uuid, "script")}
                    >
                      <SlideScriptPanel
                        initialScript={scriptBySlide.get(slide.uuid) ?? ""}
                        isRTL={rtl}
                        textOnly
                        onSave={(edited) =>
                          save(
                            section.id,
                            reconstructScriptWithSlide(
                              section.script.content,
                              slide.uuid,
                              edited
                            )
                          )
                        }
                        onDelete={() => {
                          removeSlide(slide.uuid, {
                            discardScript: true,
                          }).catch((error) => {
                            console.error("Error deleting slide", error);
                          });
                        }}
                      />
                    </div>
                  ))}
                </Flex>
              ) : (
                <Narration
                  script={section.script.content}
                  isEditable
                  index={index}
                  isRTL={rtl}
                  onEditComplete={(_i, updatedScript) =>
                    handleWholeSectionEditComplete(section.id, updatedScript)
                  }
                />
              )}
            </NonInteractiveHighlights>
          </PanelSection>
        );
      })}
    </>
  );
};

// Disable hover/click affordances on highlighted spans inside the script
// editor — they would otherwise inherit the chapter editor's pointer cursor
// and hover-color change, which look like an interaction this surface doesn't
// support. `pointer-events: none` lets the contentEditable parent handle caret
// placement and selection across the span unchanged.
const NonInteractiveHighlights = styled.div`
  .highlight-tag {
    pointer-events: none;
  }
`;
